import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { gmailApi } from '../services/api';
import type { GmailOAuthCallbackResponse } from '../types/api';

const hexToRgba = (hex: string, alpha: number) => {
  const sanitized = hex.replace('#', '');
  const isShort = sanitized.length === 3;
  const normalized = isShort
    ? sanitized
        .split('')
        .map(char => char + char)
        .join('')
    : sanitized;

  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  const clampedAlpha = Math.min(Math.max(alpha, 0), 1);
  return `rgba(${r}, ${g}, ${b}, ${clampedAlpha})`;
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => `${theme.spacing[8]} ${theme.spacing[5]}`};
`;

const Card = styled.section`
  width: min(520px, 92vw);
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => hexToRgba(theme.colors.border, theme.isLight ? 0.35 : 0.6)};
  border-radius: ${({ theme }) => theme.borders.radius['2xl']};
  box-shadow: ${({ theme }) => (theme.isLight ? theme.shadows.md : theme.shadows.lg)};
  padding: ${({ theme }) => `${theme.spacing[6]} ${theme.spacing[6]}`};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
  animation: ${fadeIn} 0.35s ease;
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  text-align: center;
`;

const Logo = styled.img`
  width: 84px;
  height: 84px;
  object-fit: contain;
`;

const StatusTag = styled.span<{ $variant: 'success' | 'loading' | 'error' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  letter-spacing: 0.06em;
  text-transform: uppercase;

  ${({ theme, $variant }) => {
    const color =
      $variant === 'success'
        ? theme.colors.primary
        : $variant === 'error'
        ? theme.colors.error
        : theme.colors.primary;
    const textColor =
      $variant === 'error' ? theme.colors.error : theme.colors.textPrimary;

    return css`
      border: 1px solid ${hexToRgba(color, theme.isLight ? 0.35 : 0.5)};
      color: ${textColor};
      background: ${hexToRgba(color, theme.isLight ? 0.14 : 0.22)};
    `;
  }}
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: clamp(1.8rem, 3vw, 2.2rem);
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Description = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => hexToRgba(theme.colors.textSecondary, theme.isLight ? 0.9 : 0.85)};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  max-width: 48ch;
`;

const Notice = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  align-items: center;
  text-align: center;
  color: ${({ theme }) => hexToRgba(theme.colors.textSecondary, theme.isLight ? 0.8 : 0.75)};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ErrorBox = styled.div`
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  border: 1px solid ${({ theme }) => hexToRgba(theme.colors.error, theme.isLight ? 0.45 : 0.55)};
  background: ${({ theme }) => hexToRgba(theme.colors.error, theme.isLight ? 0.12 : 0.18)};
  color: ${({ theme }) => theme.colors.error};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  text-align: center;
`;

const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid ${({ theme }) => hexToRgba(theme.colors.borderLight, theme.isLight ? 0.6 : 0.3)};
  margin: 0;
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ProgressLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => hexToRgba(theme.colors.textSecondary, theme.isLight ? 0.7 : 0.75)};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  border-radius: ${({ theme }) => theme.borders.radius.full};
  background: ${({ theme }) => hexToRgba(theme.colors.borderLight, theme.isLight ? 0.6 : 0.25)};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $value: number }>`
  height: 100%;
  width: ${({ $value }) => `${Math.min(Math.max($value, 0), 1) * 100}%`};
  background: ${({ theme }) =>
    `linear-gradient(90deg, ${theme.colors.primary} 0%, ${theme.colors.primaryHover ?? theme.colors.primaryLight} 100%)`};
  transition: width 0.35s ${({ theme }) => theme.animations.easing.easeInOut};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: center;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.background};
  background: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  min-width: 200px;
  transition: all 0.2s ${({ theme }) => theme.animations.easing.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover ?? theme.colors.primary};
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textPrimary};
  background: transparent;
  border: 1px solid ${({ theme }) => hexToRgba(theme.colors.border, theme.isLight ? 0.45 : 0.6)};
  min-width: 160px;
  transition: all 0.2s ${({ theme }) => theme.animations.easing.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

type ViewStatus = 'loading' | 'success' | 'error';

type ProviderType = 'gmail' | 'slack';

type ProviderConfig = {
  name: string;
  redirectPath: string;
  successSecondaryPath: string;
  successSecondaryLabel: string;
  loadingSecondaryPath: string;
  loadingSecondaryLabel: string;
  errorPrimaryPath: string;
  errorSecondaryPath: string;
  errorSecondaryLabel: string;
  statusTags: Record<ViewStatus, string>;
  titles: Record<ViewStatus, string>;
  descriptions: Record<ViewStatus, string>;
  notices: Record<ViewStatus, string>;
};

const providerConfigs: Record<ProviderType, ProviderConfig> = {
  gmail: {
    name: 'Gmail',
    redirectPath: '/gmail/config',
    successSecondaryPath: '/gmail',
    successSecondaryLabel: 'Voltar para integrações',
    loadingSecondaryPath: '/gmail',
    loadingSecondaryLabel: 'Cancelar e voltar para integrações',
    errorPrimaryPath: '/gmail/config',
    errorSecondaryPath: '/home',
    errorSecondaryLabel: 'Ir para o dashboard',
    statusTags: {
      loading: 'Sincronizando Gmail',
      success: 'Integração conectada',
      error: 'Falha na autenticação',
    },
    titles: {
      loading: 'Conectando Google Workspace',
      success: 'Conta Gmail conectada',
      error: 'Não foi possível conectar',
    },
    descriptions: {
      loading:
        'Concluindo a conexão segura com o Gmail. Você poderá acessar o painel de controle em instantes.',
      success:
        'Integração confirmada. Você será redirecionado automaticamente para o painel de controle.',
      error:
        'Algo não correu como esperado. Revise as permissões no Google Cloud e tente novamente.',
    },
    notices: {
      loading: 'Esse processo leva apenas alguns segundos.',
      success: 'Você pode aguardar o redirecionamento automático ou avançar manualmente agora.',
      error: 'Verifique sua conta Google ou tente novamente mais tarde.',
    },
  },
  slack: {
    name: 'Slack',
    redirectPath: '/slack',
    successSecondaryPath: '/slack/config',
    successSecondaryLabel: 'Ver configurações Slack',
    loadingSecondaryPath: '/slack',
    loadingSecondaryLabel: 'Cancelar e voltar para Slack',
    errorPrimaryPath: '/slack/config',
    errorSecondaryPath: '/slack',
    errorSecondaryLabel: 'Ir para canais Slack',
    statusTags: {
      loading: 'Sincronizando Slack',
      success: 'Workspace conectado',
      error: 'Falha na autenticação',
    },
    titles: {
      loading: 'Conectando Slack Workspace',
      success: 'Workspace Slack conectado',
      error: 'Não foi possível conectar',
    },
    descriptions: {
      loading:
        'Concluindo a ligação segura com o Slack. Em instantes você poderá administrar o workspace pelo painel de controle.',
      success:
        'Integração Slack confirmada. Você será redirecionado automaticamente para o painel de controle.',
      error:
        'Não conseguimos validar o token informado. Revise o Slack App e tente novamente.',
    },
    notices: {
      loading: 'Esse processo leva apenas alguns segundos.',
      success: 'Você pode aguardar o redirecionamento automático ou avançar manualmente agora.',
      error: 'Confira as credenciais do Slack App ou tente novamente mais tarde.',
    },
  },
};

interface GmailCallbackProps {
  defaultProvider?: ProviderType;
}

const GmailCallback: React.FC<GmailCallbackProps> = ({ defaultProvider = 'gmail' }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const code = searchParams.get('code');
  const stateParam = searchParams.get('state') ?? undefined;
  const authError = searchParams.get('error');
  const statusParam = searchParams.get('status');
  const emailParam = searchParams.get('email') ?? undefined;
  const userParam = searchParams.get('userId') ?? undefined;
  const expiresAtParam = searchParams.get('expiresAt') ?? undefined;
  const messageParam = searchParams.get('message') ?? undefined;
  const providerParam = (searchParams.get('provider') ?? undefined) as ProviderType | undefined;
  const workspaceParam = searchParams.get('workspace') ?? undefined;

  const provider = React.useMemo<ProviderType>(() => {
    if (providerParam && providerParam in providerConfigs) {
      return providerParam;
    }
    return defaultProvider;
  }, [defaultProvider, providerParam]);

  const providerCopy = providerConfigs[provider];

  const [status, setStatus] = React.useState<ViewStatus>('loading');
  const [responseData, setResponseData] = React.useState<GmailOAuthCallbackResponse | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [redirectCountdown, setRedirectCountdown] = React.useState(10);
  const hasRedirectedRef = React.useRef(false);

  React.useEffect(() => {
    if (!statusParam) {
      return;
    }

    const normalizedStatus =
      statusParam === 'success' ? 'success' : statusParam === 'error' ? 'error' : 'loading';

    if (normalizedStatus === 'success') {
      setStatus('success');
      setErrorMessage(null);
      setResponseData({
        email: emailParam ?? 'unknown',
        userId: userParam ?? 'default',
        expiresAt: expiresAtParam ? Number(expiresAtParam) : Math.floor(Date.now() / 1000),
      });
    } else if (normalizedStatus === 'error') {
      setStatus('error');
      setResponseData(null);
      setErrorMessage(messageParam ?? 'Não foi possível finalizar a conexão.');
    } else {
      setStatus('loading');
      setResponseData(null);
      setErrorMessage(null);
    }
  }, [emailParam, expiresAtParam, messageParam, statusParam, userParam]);

  React.useEffect(() => {
    if (statusParam) {
      return;
    }

    if (provider !== 'gmail') {
      return;
    }

    if (authError) {
      setStatus('error');
      setErrorMessage('Autorização cancelada pelo Google. Tente novamente.');
      return;
    }

    if (!code) {
      setStatus('error');
      setErrorMessage('Código de autorização não encontrado. Volte e tente novamente.');
      return;
    }

    let cancelled = false;

    setStatus('loading');
    setErrorMessage(null);

    gmailApi
      .handleOAuthCallback(code, stateParam)
      .then(response => {
        if (cancelled) return;

        if (response.status === 'success') {
          setResponseData(response.data);
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMessage(response.message || 'Não foi possível finalizar a conexão.');
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('[GMAIL CALLBACK] Erro ao processar callback:', err);
        setStatus('error');
        setErrorMessage('Ocorreu um erro ao conectar com o Gmail. Tente novamente em instantes.');
      });

    return () => {
      cancelled = true;
    };
  }, [authError, code, provider, stateParam, statusParam]);

  React.useEffect(() => {
    if (status !== 'success') {
      return;
    }

    setRedirectCountdown(10);

    const intervalId = window.setInterval(() => {
      setRedirectCountdown(prev => {
        if (prev <= 1) {
          window.clearInterval(intervalId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const timeoutId = window.setTimeout(() => {
      if (!hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        navigate(providerCopy.redirectPath, { replace: true });
      }
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [navigate, providerCopy.redirectPath, status]);

  const subtitle = React.useMemo(() => {
    if (status === 'loading') return providerCopy.descriptions.loading;
    if (status === 'success') return providerCopy.descriptions.success;
    return errorMessage ?? providerCopy.descriptions.error;
  }, [errorMessage, providerCopy.descriptions.error, providerCopy.descriptions.loading, providerCopy.descriptions.success, status]);

  const progressValue = React.useMemo(() => {
    if (status === 'success') {
      const elapsed = 10 - redirectCountdown;
      return Math.min(Math.max(elapsed / 10, 0), 1);
    }
    if (status === 'loading') {
      return 0.35;
    }
    return 0;
  }, [redirectCountdown, status]);

  const handleManualRedirect = React.useCallback(() => {
    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;
    navigate(providerCopy.redirectPath, { replace: true });
  }, [navigate, providerCopy.redirectPath]);

  const handleBackToIntegrations = React.useCallback(() => {
    hasRedirectedRef.current = true;
    navigate(providerCopy.successSecondaryPath, { replace: true });
  }, [navigate, providerCopy.successSecondaryPath]);

  const handleLoadingCancel = React.useCallback(() => {
    navigate(providerCopy.loadingSecondaryPath, { replace: true });
  }, [navigate, providerCopy.loadingSecondaryPath]);

  const handleErrorPrimary = React.useCallback(() => {
    navigate(providerCopy.errorPrimaryPath, { replace: true });
  }, [navigate, providerCopy.errorPrimaryPath]);

  const handleErrorSecondary = React.useCallback(() => {
    navigate(providerCopy.errorSecondaryPath, { replace: true });
  }, [navigate, providerCopy.errorSecondaryPath]);

  return (
    <Page>
      <Card>
        <Header>
          <Logo src="/icons/loopra_fallback.webp" alt="Loopra" />
          <StatusTag $variant={status}>{providerCopy.statusTags[status]}</StatusTag>
          <Title>{providerCopy.titles[status]}</Title>
          <Description>{subtitle}</Description>
        </Header>

        {status === 'error' && <ErrorBox>{errorMessage ?? 'Erro desconhecido'}</ErrorBox>}

        {(status === 'success' || status === 'loading') && (
          <>
            <Divider />
            <ProgressSection>
              <ProgressLabel>
                {status === 'success'
                  ? `Redirecionando para o painel de controle em ${redirectCountdown}s`
                  : `Sincronizando credenciais ${providerCopy.name}`}
              </ProgressLabel>
              <ProgressBar>
                <ProgressFill $value={progressValue} />
              </ProgressBar>
            </ProgressSection>
          </>
        )}

        <Notice>
          {status === 'success' &&
            (provider === 'slack' && workspaceParam
              ? `Workspace ${workspaceParam} conectado com sucesso. ${providerCopy.notices.success}`
              : providerCopy.notices.success)}
          {status === 'loading' && providerCopy.notices.loading}
          {status === 'error' && (errorMessage ?? providerCopy.notices.error)}
        </Notice>

        <Actions>
          {status === 'success' && (
            <>
              <PrimaryButton onClick={handleManualRedirect}>Ir para painel de controle</PrimaryButton>
              <SecondaryButton onClick={handleBackToIntegrations}>
                {providerCopy.successSecondaryLabel}
              </SecondaryButton>
            </>
          )}

          {status === 'loading' && (
            <SecondaryButton onClick={handleLoadingCancel}>
              {providerCopy.loadingSecondaryLabel}
            </SecondaryButton>
          )}

          {status === 'error' && (
            <>
              <PrimaryButton onClick={handleErrorPrimary}>Tentar novamente</PrimaryButton>
              <SecondaryButton onClick={handleErrorSecondary}>
                {providerCopy.errorSecondaryLabel}
              </SecondaryButton>
            </>
          )}
        </Actions>
      </Card>
    </Page>
  );
};

export default GmailCallback;
